import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Search, Upload, Download, Phone, Mail, Eye, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOURCE_LABELS = { facebook: "Facebook", cold_call: "Cold Call", referral: "Referral", website: "Website", manual: "Manual" };
const STAGE_LABELS = { new_lead: "New Lead", contacted: "Contacted", in_negotiation: "Negotiation", will_call_when_needed: "Will Call When Needed", closed_won: "Won", closed_lost: "Lost" };
const STAGE_COLORS = { new_lead: "bg-blue-100 text-blue-800", contacted: "bg-yellow-100 text-yellow-800", in_negotiation: "bg-orange-100 text-orange-800", will_call_when_needed: "bg-purple-100 text-purple-800", closed_won: "bg-green-100 text-green-800", closed_lost: "bg-gray-100 text-gray-500" };

const emptyForm = { name: "", email: "", phone: "", company: "", source: "manual", notes: "", pipeline_stage: "new_lead",
  needs_condition: "any", needs_engine_type: "any", needs_mast_stages: "", needs_tire_type: "any", needs_drive: "any",
  needs_capacity_min: "", needs_capacity_max: "", needs_budget_max: "", needs_description: "" };

function daysSince(isoDate) {
  if (!isoDate) return 999;
  const d = new Date(isoDate);
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

function FollowUpBadge({ lastContact }) {
  const days = daysSince(lastContact);
  if (days >= 5) return <Badge className="bg-red-100 text-red-700 border-none text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />Urgent {days}d</Badge>;
  if (days >= 3) return <Badge className="bg-orange-100 text-orange-700 border-none text-[10px]"><Clock className="w-3 h-3 mr-0.5" />{days}d ago</Badge>;
  return <span className="text-xs text-gray-400">{days === 0 ? "Today" : `${days}d ago`}</span>;
}

function InlineDescription({ client, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(client.short_description || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(client.short_description || ""); }, [client.short_description]);

  const save = async () => {
    const trimmed = value.trim();
    if (trimmed === (client.short_description || "")) { setEditing(false); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/crm/clients/${client.id}`, { short_description: trimmed }, { withCredentials: true });
      toast.success("Description saved");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error("Could not save description");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        type="text"
        maxLength={80}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") { setValue(client.short_description || ""); setEditing(false); }
        }}
        disabled={saving}
        placeholder="e.g. Looking for electric forklift..."
        data-testid={`client-desc-input-${client.id}`}
        className="mt-1 w-full text-xs text-gray-700 bg-yellow-50 border border-yellow-300 rounded-sm px-1.5 py-0.5 focus:outline-none focus:border-yellow-500"
      />
    );
  }

  const text = (client.short_description || "").trim();
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid={`client-desc-${client.id}`}
      title="Click to edit"
      className={`mt-1 text-left text-xs italic block max-w-[260px] truncate hover:bg-yellow-50 rounded-sm px-1 py-0.5 -mx-1 transition-colors ${text ? "text-gray-700" : "text-gray-300"}`}
    >
      {text || "+ Add description"}
    </button>
  );
}

export default function CRMClients() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState(searchParams.get("stage") || "all");
  const [attentionFilter, setAttentionFilter] = useState(searchParams.get("attention") === "true");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchClients = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (sourceFilter !== "all") params.source = sourceFilter;
      if (stageFilter !== "all") params.pipeline_stage = stageFilter;
      const { data } = await axios.get(`${API}/crm/clients`, { params, withCredentials: true });
      let filtered = data;
      if (attentionFilter) {
        filtered = data.filter(c => {
          const days = daysSince(c.last_contact);
          // Only surface follow-ups for clients in the "Negotiation" stage.
          return days >= 3 && c.pipeline_stage === "in_negotiation";
        });
      }
      setClients(filtered);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, sourceFilter, stageFilter, attentionFilter]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleChange = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm }); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name||"", email: c.email||"", phone: c.phone||"", company: c.company||"", source: c.source||"manual", notes: c.notes||"", pipeline_stage: c.pipeline_stage||"new_lead" });
    setDialogOpen(true);
  };

  const openDetail = (client) => {
    navigate(`/admin/crm/clients/${client.id}`);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/crm/clients/${editing.id}`, {
          name: form.name, email: form.email, phone: form.phone, company: form.company,
          source: form.source, notes: form.notes, pipeline_stage: form.pipeline_stage,
        }, { withCredentials: true });
        toast.success("Client updated");
      } else {
        const body = {
          ...form,
          needs_mast_stages: form.needs_mast_stages ? parseInt(form.needs_mast_stages) : null,
          needs_capacity_min: form.needs_capacity_min ? parseInt(form.needs_capacity_min) : null,
          needs_capacity_max: form.needs_capacity_max ? parseInt(form.needs_capacity_max) : null,
          needs_budget_max: form.needs_budget_max ? parseFloat(form.needs_budget_max) : null,
          needs_condition: form.needs_condition !== "any" ? form.needs_condition : null,
          needs_engine_type: form.needs_engine_type !== "any" ? form.needs_engine_type : null,
          needs_tire_type: form.needs_tire_type !== "any" ? form.needs_tire_type : null,
          needs_drive: form.needs_drive !== "any" ? form.needs_drive : null,
        };
        const { data } = await axios.post(`${API}/crm/clients`, body, { withCredentials: true });
        if (data.created_request) {
          toast.success(`Client added + ForkFinder request created (${data.created_request.id.slice(0,8)})`);
        } else {
          toast.success("Client added");
        }
      }
      setDialogOpen(false);
      fetchClients();
    } catch (e) { toast.error("Error saving client"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/crm/clients/${id}`, { withCredentials: true });
      toast.success("Client deleted");
      setDeleteTarget(null);
      fetchClients();
    } catch { toast.error("Error deleting"); }
  };

  const handleExport = () => { window.open(`${API}/crm/export-csv`, "_blank"); };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/crm/import-csv`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} clients`);
      fetchClients();
    } catch { toast.error("Import failed"); }
    e.target.value = "";
  };

  return (
    <div data-testid="crm-clients" className="p-4 sm:p-8 pb-24 sm:pb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Clients</h1>
          {attentionFilter && <p className="text-sm text-orange-600 font-medium mt-1">Showing Negotiation clients not contacted in 3+ days</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-sm cursor-pointer">
            <Upload className="w-3.5 h-3.5" /> Import
            <input type="file" accept=".csv" className="hidden" onChange={handleImport} />
          </label>
          <button onClick={handleExport} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-sm">
            <Download className="w-3.5 h-3.5" /> Export
          </button>
          <button data-testid="add-crm-client-btn" onClick={openCreate} className="inline-flex items-center gap-1.5 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 px-4 py-2 rounded-sm text-sm">
            <Plus className="w-4 h-4" /> Add Client
          </button>
        </div>
      </div>

      {/* Mobile-only floating action button — positioned above the "Made with Emergent" badge */}
      <button
        data-testid="add-crm-client-fab"
        onClick={openCreate}
        aria-label="Add Client"
        className="sm:hidden fixed bottom-24 right-4 z-50 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-full shadow-xl w-14 h-14 flex items-center justify-center transition-transform active:scale-95"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && fetchClients()} placeholder="Search..." className="pl-9 bg-white" />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-32"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="facebook">Facebook</SelectItem>
            <SelectItem value="cold_call">Cold Call</SelectItem>
            <SelectItem value="referral">Referral</SelectItem>
            <SelectItem value="website">Website</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
        <Select value={stageFilter} onValueChange={(v) => { setStageFilter(v); setAttentionFilter(false); }}>
          <SelectTrigger className="w-32"><SelectValue placeholder="Stage" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            <SelectItem value="new_lead">New Lead</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="in_negotiation">Negotiation</SelectItem>
            <SelectItem value="closed_won">Won</SelectItem>
            <SelectItem value="closed_lost">Lost</SelectItem>
          </SelectContent>
        </Select>
        <button
          onClick={() => { setAttentionFilter(!attentionFilter); setStageFilter("all"); }}
          className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-sm border transition-colors ${attentionFilter ? "bg-orange-100 border-orange-300 text-orange-800" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
        >
          <AlertTriangle className="w-3.5 h-3.5" /> Needs Follow-up
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="font-semibold">Name</TableHead>
              <TableHead className="font-semibold">Contact</TableHead>
              <TableHead className="font-semibold">Source</TableHead>
              <TableHead className="font-semibold">Stage</TableHead>
              <TableHead className="font-semibold">Last Contact</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
            ) : clients.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">No clients found</TableCell></TableRow>
            ) : clients.map(c => (
              <TableRow key={c.id} className={daysSince(c.last_contact) >= 5 && !["closed_won","closed_lost"].includes(c.pipeline_stage) ? "bg-red-50/50" : ""}>
                <TableCell>
                  <button onClick={() => openDetail(c)} className="text-left hover:text-yellow-700 transition-colors">
                    <p className="font-medium text-gray-900">{c.name}</p>
                    {c.company && <p className="text-xs text-gray-500">{c.company}</p>}
                  </button>
                  <InlineDescription client={c} onSaved={fetchClients} />
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-0.5 text-xs text-gray-600">
                    {c.email && c.email.indexOf("@noemail") === -1 && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                    {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                  </div>
                </TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{SOURCE_LABELS[c.source] || c.source}</Badge></TableCell>
                <TableCell>
                  <Select value={c.pipeline_stage} onValueChange={async (v) => {
                    await axios.patch(`${API}/crm/clients/${c.id}/pipeline`, { pipeline_stage: v }, { withCredentials: true });
                    fetchClients();
                  }}>
                    <SelectTrigger className="h-7 w-28 p-0 border-none">
                      <Badge className={`${STAGE_COLORS[c.pipeline_stage]} border-none text-xs`}>{STAGE_LABELS[c.pipeline_stage]}</Badge>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new_lead">New Lead</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="in_negotiation">Negotiation</SelectItem>
                      <SelectItem value="closed_won">Won</SelectItem>
                      <SelectItem value="closed_lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell><FollowUpBadge lastContact={c.last_contact} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openDetail(c)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-sm"><Eye className="w-4 h-4" /></button>
                    <button onClick={() => openEdit(c)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-sm"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => setDeleteTarget(c)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-sm"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Client" : "Add Client"}</DialogTitle>
            <DialogDescription>{editing ? "Update client details." : "Add a new client to your CRM."}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div className="sm:col-span-2"><label className="text-sm font-semibold text-gray-700 mb-1 block">Name *</label><Input value={form.name} onChange={e => handleChange("name", e.target.value)} /></div>
            <div><label className="text-sm font-semibold text-gray-700 mb-1 block">Email</label><Input value={form.email} onChange={e => handleChange("email", e.target.value)} /></div>
            <div><label className="text-sm font-semibold text-gray-700 mb-1 block">Phone</label><Input value={form.phone} onChange={e => handleChange("phone", e.target.value)} /></div>
            <div><label className="text-sm font-semibold text-gray-700 mb-1 block">Company</label><Input value={form.company} onChange={e => handleChange("company", e.target.value)} /></div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Source</label>
              <Select value={form.source} onValueChange={v => handleChange("source", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="facebook">Facebook Marketplace</SelectItem>
                  <SelectItem value="cold_call">Cold Call</SelectItem>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Pipeline Stage</label>
              <Select value={form.pipeline_stage} onValueChange={v => handleChange("pipeline_stage", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_lead">New Lead</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="in_negotiation">Negotiation</SelectItem>
                  <SelectItem value="closed_won">Won</SelectItem>
                  <SelectItem value="closed_lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><label className="text-sm font-semibold text-gray-700 mb-1 block">Notes</label><Textarea value={form.notes} onChange={e => handleChange("notes", e.target.value)} /></div>

            {/* Forklift Needs - only on create */}
            {!editing && (
              <div className="sm:col-span-2 border-t border-gray-200 pt-4 mt-2">
                <p className="text-sm font-bold text-gray-900 mb-1">What does the client need?</p>
                <p className="text-xs text-gray-500 mb-3">Fill in forklift criteria to auto-create a ForkFinder request.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Condition</label>
                    <Select value={form.needs_condition} onValueChange={v => handleChange("needs_condition", v)}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="used">Used</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Engine</label>
                    <Select value={form.needs_engine_type} onValueChange={v => handleChange("needs_engine_type", v)}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="propane">Propane</SelectItem>
                        <SelectItem value="diesel">Diesel</SelectItem>
                        <SelectItem value="electric">Electric</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Mast</label>
                    <Select value={form.needs_mast_stages || "any"} onValueChange={v => handleChange("needs_mast_stages", v === "any" ? "" : v)}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="2">2 Stages</SelectItem>
                        <SelectItem value="3">3 Stages</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Tires</label>
                    <Select value={form.needs_tire_type} onValueChange={v => handleChange("needs_tire_type", v)}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="pneumatic">Pneumatic</SelectItem>
                        <SelectItem value="cushion">Cushion</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Drive</label>
                    <Select value={form.needs_drive} onValueChange={v => handleChange("needs_drive", v)}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="2wd">2WD</SelectItem>
                        <SelectItem value="4wd">4WD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Budget Max ($)</label>
                    <Input type="number" className="h-9" value={form.needs_budget_max} onChange={e => handleChange("needs_budget_max", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Capacity Min (lbs)</label>
                    <Input type="number" className="h-9" value={form.needs_capacity_min} onChange={e => handleChange("needs_capacity_min", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Capacity Max (lbs)</label>
                    <Input type="number" className="h-9" value={form.needs_capacity_max} onChange={e => handleChange("needs_capacity_max", e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Details</label>
                    <Input className="h-9" value={form.needs_description} onChange={e => handleChange("needs_description", e.target.value)} placeholder="e.g., Needs it for warehouse..." />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <button onClick={() => setDialogOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-sm border border-gray-200">Cancel</button>
            <button onClick={handleSave} className="px-4 py-2 text-sm font-bold bg-yellow-400 text-gray-900 hover:bg-yellow-500 rounded-sm">{editing ? "Update" : "Add Client"}</button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Client</AlertDialogTitle><AlertDialogDescription>Delete <strong>{deleteTarget?.name}</strong> and all their interactions? This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteTarget?.id)} className="bg-red-600 text-white hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
